import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import InstitutionLogo from '../components/InstitutionLogo';
import api, { formatDate, formatTime, relativeDay } from '../utils/api';
import { useEvents, useInstitutions } from '../lib/queries';
import { Button } from '../components/ui/button';
import AppSelect from '../components/ui/app-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ArrowRight, CalendarDays, CalendarPlus, Check, Clock3, Ellipsis, ExternalLink, MapPin, Plus, Search, Star, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import OwnDateTimePicker from './OwnDateTimePicker';

const STATUS_LABELS = {
  event_created: 'Created',
  event_scheduled: 'Scheduled',
  shoot_completed: 'Shoot complete',
  delivery_in_progress: 'In delivery',
  closed: 'Closed'
};
const PRIORITY_LABELS = { normal: 'Normal', high: 'High', vip: 'VIP' };
const EVENT_TYPES = [
  { value: 'cultural', label: 'Cultural' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'sports', label: 'Sports' },
  { value: 'inauguration', label: 'Inauguration' },
  { value: 'convocation', label: 'Convocation' },
  { value: 'press_meet', label: 'Press meet' },
  { value: 'other', label: 'Other' }
];
const REQUIREMENT_OPTIONS = ['photos', 'video_coverage', 'highlight_video', 'instagram_reel', 'live_stream', 'drone'];
const time = (date) => new Date(date || 0).getTime() || 0;
function StatusPill({ status }) {
  return (
    <span className={`event-status status-${status}`} data-testid="event-status-badge">
      <i />
      {STATUS_LABELS[status] || status?.replaceAll('_', ' ') || 'Not set'}
    </span>
  );
}

function PriorityPill({ priority }) {
  return (
    <span className={`event-status priority-${priority || 'normal'}`}>
      {PRIORITY_LABELS[priority] || 'Normal'}
    </span>
  );
}

const Events = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const { data: events = [], isPending: eventsPending, isError, error: loadError } = useEvents();
  const { data: institutions = [], isPending: institutionsPending } = useInstitutions();
  const loading = eventsPending || institutionsPending;
  const [creating, setCreating] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    institution: 'all',
    priority: 'all',
    search: ''
  });
  const [newEvent, setNewEvent] = useState({
    title: '',
    institution_id: '',
    department: '',
    event_date_start: '',
    event_date_end: '',
    venue: '',
    description: '',
    event_type: 'seminar',
    expected_audience: '',
    chief_guests: '',
    requirements: [],
    comments: '',
    priority: 'normal',
    deliverable_due_date: ''
  });

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load events', {
        description: loadError?.response?.data?.detail || 'Please try again'
      });
    }
  }, [isError, loadError]);

  // Open create dialog when arriving with ?create=new
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === 'new') {
      setIsCreateDialogOpen(true);
      navigate('/events', { replace: true });
    }
  }, [location.search, navigate]);

  const handleCreateEvent = async () => {
    if (creating) return; // Prevent double-click

    try {
      // Validate required fields
      if (!newEvent.title || !newEvent.institution_id || !newEvent.event_date_start) {
        toast.error('Please fill in required fields', {
          description: 'Title, Institution, and Event Date are required'
        });
        return;
      }

      setCreating(true);

      // Prepare event data with proper date formatting
      const eventData = {
        ...newEvent,
        event_date_start: newEvent.event_date_start ? new Date(newEvent.event_date_start).toISOString() : null,
        event_date_end: newEvent.event_date_end ? new Date(newEvent.event_date_end).toISOString() : null,
        deliverable_due_date: newEvent.deliverable_due_date ? new Date(newEvent.deliverable_due_date).toISOString() : null,
        expected_audience: newEvent.expected_audience ? parseInt(newEvent.expected_audience) : null
      };

      await api.post('/events', eventData);
      toast.success('Event created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Please check all required fields and try again';
      toast.error('Failed to create event', {
        description: errorMessage
      });
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewEvent({
      title: '',
      institution_id: '',
      department: '',
      event_date_start: '',
      event_date_end: '',
      venue: '',
      description: '',
      event_type: 'seminar',
      expected_audience: '',
      chief_guests: '',
      requirements: [],
      comments: '',
      priority: 'normal',
      deliverable_due_date: ''
    });
  };

  const handleRequirementToggle = (requirement) => {
    setNewEvent(prev => ({
      ...prev,
      requirements: prev.requirements.includes(requirement)
        ? prev.requirements.filter(r => r !== requirement)
        : [...prev.requirements, requirement]
    }));
  };

  const handleDeleteEvent = async () => {
    try {
      await api.delete(`/events/${eventToDelete.id}`);
      toast.success('Event deleted successfully', {
        description: 'All associated tasks and allocations have been removed'
      });
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } catch (error) {
      toast.error('Failed to delete event', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const query = filters.search.trim().toLowerCase();
  const now = Date.now();
  const isUpcoming = (e) => e.status !== 'closed' && time(e.event_date_start) >= now;
  const upcoming = events.filter(isUpcoming).sort((x, y) => time(x.event_date_start) - time(y.event_date_start));
  const nextEvent = upcoming[0];

  const TABS = [
    { id: 'all', label: 'All', count: events.length },
    { id: 'upcoming', label: 'Upcoming', count: upcoming.length },
    { id: 'delivery', label: 'In delivery', count: events.filter((e) => e.status === 'delivery_in_progress').length },
    { id: 'closed', label: 'Closed', count: events.filter((e) => e.status === 'closed').length }
  ];
  const matchesTab = (e) =>
    tab === 'all' ||
    (tab === 'upcoming' && isUpcoming(e)) ||
    (tab === 'delivery' && e.status === 'delivery_in_progress') ||
    (tab === 'closed' && e.status === 'closed');

  const filtered = [...events]
    .filter(
      (e) =>
        matchesTab(e) &&
        (filters.priority === 'all' || e.priority === filters.priority) &&
        (filters.institution === 'all' || e.institution_id === filters.institution) &&
        (!query ||
          e.title.toLowerCase().includes(query) ||
          e.institution_name?.toLowerCase().includes(query) ||
          e.venue?.toLowerCase().includes(query))
    )
    .sort((x, y) =>
      tab === 'upcoming'
        ? time(x.event_date_start) - time(y.event_date_start)
        : time(y.event_date_start) - time(x.event_date_start)
    );
  const hasFilters =
    filters.search || tab !== 'all' || filters.priority !== 'all' || filters.institution !== 'all';
  const showHero = nextEvent && (tab === 'all' || tab === 'upcoming') && !filters.search &&
    filters.priority === 'all' && filters.institution === 'all';

  const groups = [];
  filtered.forEach((event) => {
    const d = new Date(event.event_date_start);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = {
        key,
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        items: []
      };
      groups.push(group);
    }
    group.items.push(event);
  });
  const clearFilters = () => {
    setTab('all');
    setFilters({ status: 'all', institution: 'all', priority: 'all', search: '' });
  };
  const setFilter = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });
  const openEvent = (event) => navigate(`/events/${event.id}`);

  const renderMenu = (event) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="row-menu-trigger"
          aria-label={`Actions for ${event.title}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Ellipsis size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[150px]"
        // Portal clicks still bubble through React to the clickable event card.
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem onSelect={() => openEvent(event)}>
          <ExternalLink size={15} />
          Open event
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`delete-event-button-${event.id}`}
          className="text-red-600 focus:text-red-700"
          onSelect={() => {
            setEventToDelete(event);
            setDeleteDialogOpen(true);
          }}
        >
          <Trash2 size={15} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const requirementChips = (event, limit = 3) => {
    const reqs = event.requirements || [];
    if (!reqs.length) return null;
    return (
      <div className="req-chips">
        {reqs.slice(0, limit).map((req) => (
          <span key={req}>{req.replaceAll('_', ' ')}</span>
        ))}
        {reqs.length > limit && <span>+{reqs.length - limit}</span>}
      </div>
    );
  };

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1>Events</h1>
            <p>Every shoot, from first request to final delivery.</p>
          </div>
          <div className="dashboard-heading-actions">
            <Button data-testid="create-event-button" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus size={17} />
              Create event
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading events</h2>
            <p>Gathering your schedule.</p>
          </div>
        ) : (
          <>
            {showHero && (
              <section className="next-hero" aria-label="Next event" data-testid="next-event-card">
                <div className="next-hero-main">
                  <span className="next-hero-tag">
                    <i /> Up next · {relativeDay(nextEvent.event_date_start)}
                  </span>
                  <h2>{nextEvent.title}</h2>
                  <div className="next-hero-meta">
                    <span className="next-hero-inst">
                      <InstitutionLogo name={nextEvent.institution_name} size={30} className="on-dark" />
                      {nextEvent.institution_name || 'No institution'}
                    </span>
                    <span>
                      <CalendarDays size={15} />
                      {formatDate(nextEvent.event_date_start)}
                    </span>
                    <span>
                      <Clock3 size={15} />
                      {formatTime(nextEvent.event_date_start)}
                    </span>
                    {nextEvent.venue && (
                      <span>
                        <MapPin size={15} />
                        {nextEvent.venue}
                      </span>
                    )}
                  </div>
                  {requirementChips(nextEvent, 4)}
                </div>
                <Button className="next-hero-cta" onClick={() => openEvent(nextEvent)}>
                  View event
                  <ArrowRight size={16} />
                </Button>
              </section>
            )}

            <section className="dashboard-panel inst-panel" aria-labelledby="events-list-title">
              <h2 id="events-list-title" className="sr-only">All events</h2>
              <div className="events-toolbar-row">
                <div className="tab-bar" role="tablist" aria-label="Event status">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={tab === t.id}
                      data-testid={`tab-${t.id}`}
                      className={tab === t.id ? 'is-active' : ''}
                      onClick={() => setTab(t.id)}
                    >
                      {t.label}
                      <span>{t.count}</span>
                    </button>
                  ))}
                </div>
                <div className="toolbar-controls">
                  <AppSelect
                    data-testid="filter-priority"
                    aria-label="Filter by priority"
                    value={filters.priority}
                    onChange={setFilter('priority')}
                    options={[
                      { value: 'all', label: 'Any priority' },
                      ...Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }))
                    ]}
                  />
                  <AppSelect
                    aria-label="Filter by institution"
                    value={filters.institution}
                    onChange={setFilter('institution')}
                    options={[
                      { value: 'all', label: 'All institutions' },
                      ...[...institutions]
                        .sort((x, y) => x.name.localeCompare(y.name))
                        .map((inst) => ({ value: inst.id, label: inst.name }))
                    ]}
                  />
                  <div className="event-search inst-search">
                    <Search size={18} aria-hidden="true" />
                    <input
                      data-testid="search-events-input"
                      aria-label="Search events"
                      type="search"
                      placeholder="Search events"
                      value={filters.search}
                      onChange={setFilter('search')}
                    />
                    {filters.search && (
                      <button onClick={() => setFilters({ ...filters, search: '' })} aria-label="Clear search">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {filtered.length ? (
                <div className="agenda">
                  {groups.map((group) => (
                    <section key={group.key} className="agenda-group" aria-label={group.label}>
                      <h3 className="agenda-month">
                        {group.label}
                        <span className="count-pill">{group.items.length}</span>
                      </h3>
                      <ul className="agenda-list">
                        {group.items.map((event) => {
                          const start = new Date(event.event_date_start);
                          const rel = relativeDay(event.event_date_start);
                          return (
                            <li key={event.id}>
                              <article
                                className={`agenda-item accent-${event.status}`}
                                data-testid={`event-card-${event.id}`}
                                onClick={() => openEvent(event)}
                              >
                                <div className={`agenda-date ${rel === 'Today' ? 'is-today' : ''}`} aria-hidden="true">
                                  <strong>{String(start.getDate()).padStart(2, '0')}</strong>
                                  <span>{start.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                </div>
                                <InstitutionLogo
                                  name={event.institution_name}
                                  size={48}
                                  className="agenda-logo"
                                />
                                <div className="agenda-body">
                                  <div className="agenda-title-row">
                                    <Link
                                      className="agenda-title"
                                      to={`/events/${event.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {event.title}
                                    </Link>
                                    {event.priority === 'vip' && <Star size={14} className="vip-star" aria-label="VIP" />}
                                  </div>
                                  <div className="agenda-meta">
                                    <span className="agenda-inst">{event.institution_name || 'No institution'}</span>
                                    <span><Clock3 size={14} />{formatTime(event.event_date_start)}</span>
                                    {event.venue && <span><MapPin size={14} />{event.venue}</span>}
                                  </div>
                                  {requirementChips(event)}
                                </div>
                                <div className="agenda-side">
                                  <span className={`rel-day ${rel === 'Today' ? 'is-today' : ''}`}>{rel}</span>
                                  <div className="agenda-pills">
                                    {event.priority && event.priority !== 'normal' && (
                                      <PriorityPill priority={event.priority} />
                                    )}
                                    <StatusPill status={event.status} />
                                  </div>
                                </div>
                                <div className="agenda-menu">{renderMenu(event)}</div>
                              </article>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="dashboard-empty">
                  <CalendarDays size={28} />
                  <h3>{hasFilters ? 'No matching events' : 'No events yet'}</h3>
                  <p>
                    {hasFilters
                      ? 'Try a different search or clear your filters.'
                      : 'Create your first event to start planning.'}
                  </p>
                  {hasFilters ? (
                    <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
                  ) : (
                    <Button onClick={() => setIsCreateDialogOpen(true)}>Create event</Button>
                  )}
                </div>
              )}

              <div className="table-footer">
                <span role="status" aria-live="polite">
                  {filtered.length === events.length
                    ? `${events.length} events`
                    : `${filtered.length} of ${events.length} events`}
                </span>
              </div>
            </section>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Event?"
        description={`Are you sure you want to delete "${eventToDelete?.title}"? This will also delete all associated tasks and equipment allocations. This action cannot be undone.`}
        onConfirm={handleDeleteEvent}
        confirmText="Delete Event"
      />

      {/* Create Event Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent data-testid="create-event-dialog" className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Create event</DialogTitle>
          </DialogHeader>
          <div className="sheet-head">
            <span className="sheet-icon">
              <CalendarPlus size={22} strokeWidth={1.7} />
            </span>
            <div>
              <h3>Create event</h3>
              <p>Plan a shoot and note what needs to be delivered.</p>
            </div>
          </div>

          <form
            className="sheet-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateEvent();
            }}
          >
            <section className="form-section" aria-labelledby="ev-basics">
              <div className="form-section-head">
                <h2 id="ev-basics">Basics</h2>
                <p>What the event is and who it is for.</p>
              </div>
              <div className="field">
                <label htmlFor="title">
                  Event title <i className="req">*</i>
                </label>
                <input
                  id="title"
                  data-testid="event-title-input"
                  className="field-input"
                  placeholder="Annual Graduation Ceremony 2026"
                  autoComplete="off"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="institution">
                    Institution <i className="req">*</i>
                  </label>
                  <AppSelect
                    id="institution"
                    variant="field"
                    data-testid="event-institution-select"
                    placeholder="Select institution"
                    value={newEvent.institution_id}
                    onChange={(e) => setNewEvent({ ...newEvent, institution_id: e.target.value })}
                    options={[...institutions]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((inst) => ({ value: inst.id, label: inst.name }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="department">Department</label>
                  <input
                    id="department"
                    className="field-input"
                    placeholder="Computer Science"
                    autoComplete="off"
                    value={newEvent.department}
                    onChange={(e) => setNewEvent({ ...newEvent, department: e.target.value })}
                  />
                </div>
              </div>
            </section>

            <section className="form-section" aria-labelledby="ev-when">
              <div className="form-section-head">
                <h2 id="ev-when">When and where</h2>
                <p>Pick the start, and an end time if it runs longer.</p>
              </div>
              <div className="field">
                <label>
                  Event duration <i className="req">*</i>
                </label>
                <div className="picker-box">
                  <OwnDateTimePicker
                    value={{
                      from: newEvent.event_date_start ? new Date(newEvent.event_date_start) : null,
                      to: newEvent.event_date_end ? new Date(newEvent.event_date_end) : null,
                    }}
                    onChange={({ from, to }) => {
                      setNewEvent((prev) => ({
                        ...prev,
                        event_date_start: from ? from.toISOString() : '',
                        event_date_end: to ? to.toISOString() : '',
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="venue">Venue</label>
                <input
                  id="venue"
                  className="field-input"
                  placeholder="Main Auditorium"
                  autoComplete="off"
                  value={newEvent.venue}
                  onChange={(e) => setNewEvent({ ...newEvent, venue: e.target.value })}
                />
              </div>
            </section>

            <section className="form-section" aria-labelledby="ev-type">
              <div className="form-section-head">
                <h2 id="ev-type">Type and priority</h2>
                <p>Helps the team plan coverage.</p>
              </div>
              <div className="field">
                <label id="ev-type-label">Event type</label>
                <div className="chip-radio" role="radiogroup" aria-labelledby="ev-type-label">
                  {EVENT_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.value}
                      role="radio"
                      aria-checked={newEvent.event_type === t.value}
                      className={newEvent.event_type === t.value ? 'is-selected' : ''}
                      onClick={() => setNewEvent({ ...newEvent, event_type: t.value })}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label id="ev-priority-label">Priority</label>
                <div className="chip-radio" role="radiogroup" aria-labelledby="ev-priority-label">
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      role="radio"
                      aria-checked={newEvent.priority === value}
                      className={`${newEvent.priority === value ? 'is-selected' : ''} priority-chip-${value}`}
                      onClick={() => setNewEvent({ ...newEvent, priority: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="form-section" aria-labelledby="ev-req">
              <div className="form-section-head">
                <h2 id="ev-req">Requirements</h2>
                <p>Select everything that should be delivered.</p>
              </div>
              <div className="chip-radio" role="group" aria-labelledby="ev-req">
                {REQUIREMENT_OPTIONS.map((req) => {
                  const selected = newEvent.requirements.includes(req);
                  return (
                    <button
                      type="button"
                      key={req}
                      role="checkbox"
                      aria-checked={selected}
                      className={selected ? 'is-selected' : ''}
                      onClick={() => handleRequirementToggle(req)}
                    >
                      {selected && <Check size={14} strokeWidth={2.6} />}
                      {req.replaceAll('_', ' ')}
                    </button>
                  );
                })}
              </div>
              <div className="field field-spaced">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  className="field-input field-textarea"
                  rows={3}
                  placeholder="Add any notes the team should know"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                />
              </div>
            </section>

            <div className="form-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-create-event-button" disabled={creating}>
                {creating ? 'Creating…' : 'Create event'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Events;
